import { Component, Inject } from '@angular/core';

import { HsgModalDefault, HSG_MODAL_DATA, HsgModalRef } from '@hhsg/hsg-asc';
import { ShiftBlockService } from 'src/shared/services/shift-block.service';
import * as moment from 'moment';
import { Constants } from 'src/shared/utilities/constants.util';
import { ShiftBlock } from 'src/shared/models/shift-block.model';
import { JobTitle } from 'src/shared/models/job-title.model';
import { InpatientCell } from 'src/app/smt/staffing/staffing-grid/models/inpatient-cell.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { CostCenter } from 'src/shared/models/cost-center.model';
import { StaffingGridConstants } from 'src/app/smt/staffing/staffing-grid/utilities/staffing-grid-constants.util';

@Component({
  selector: 'smt-current-block-staffing-grid-modal',
  templateUrl: './current-block-staffing-grid-modal.component.html',
  styleUrls: ['./current-block-staffing-grid-modal.component.scss']
})

export class CurrentBlockStaffingGridModalComponent extends HsgModalDefault {
  loading: boolean = true;
  nextShiftBlock: ShiftBlock;
  shiftBlocks: ShiftBlock[] = [];
  variableJobTitles: JobTitle[] = [];
  maxCensus: number;
  minCensus: number;
  staffingGrid: InpatientCell[][] = [];
  isRedrawn: boolean = false;
  nextShiftBlockCostcenter: CostCenter;

  constructor(
    public modalRef: HsgModalRef<HsgModalDefault>,
    @Inject(HSG_MODAL_DATA) public data: any,
    protected _shiftBlockService: ShiftBlockService) 
  {
    super(modalRef, data);
    
    this.getNextShiftBlock();    
  }

  private getNextShiftBlock() {
    let nextShiftBlockTime = this.formatNextTime();

    this._shiftBlockService.loadShiftBlock(this.data.shiftRecord.costCenterId, nextShiftBlockTime).then(
      (nextShiftBlock: any) => {
        this.loading = false;
        this.nextShiftBlock = nextShiftBlock;
        this.nextShiftBlockCostcenter = nextShiftBlock.costCenter;

        this.initializeGridData();
      }
    );
  }

  private initializeGridData() {
    this.variableJobTitles = this.data.shiftRecord.costCenter.variableJobTitles;
    this.maxCensus = this.data.shiftRecord.costCenter.inpatientConfiguration.maxCensus;
    this.minCensus = this.data.shiftRecord.costCenter.inpatientConfiguration.minCensus;
    this.checkIsNextShiftblockRedrawn();

    this.shiftBlocks.push(this.data.shiftRecord.shiftBlock);
    if (!this.isRedrawn)
      this.shiftBlocks.push(this.nextShiftBlock);

    this.setShiftBlockLabels();

    this.buildVariableStaffingGrid();
  }

  checkIsNextShiftblockRedrawn() {
    let currentShiftBlockCreated = new Date(this.data.shiftRecord.shiftBlock.created).getTime();
    let nextShiftBlockCreated = new Date(this.nextShiftBlock.created).getTime();
    
    if (nextShiftBlockCreated/1000 - currentShiftBlockCreated/1000 > 10)
      this.isRedrawn = true;
  }

  private setShiftBlockLabels() {
    this.shiftBlocks.forEach(s => {
      s.description = MiscUtil.getShiftBlockDescription(s.startHour, s.endHour);
    });

    this.nextShiftBlock.description = MiscUtil.getShiftBlockDescription(this.nextShiftBlock.startHour, this.nextShiftBlock.endHour);
  }

  // Returns the next shift block for inpatient.  Each shift block on an inpatient cost center is 4 hours long.
  // If that shift block spans the start of daylight saving time then we cannot use moment and add hours.
  // Thus, this function will manually add hours to the workflowTimeFacility to ensure that the end result is
  // desired, even if it spans daylight saving time.
  private formatNextTime(): string {    
    let workflowTime = moment(this.data.shiftRecord.workflowTimeFacility);
    let startHour = workflowTime.hour() + StaffingGridConstants.DefaultShiftBlockLength;

    // Wrap to the next day if the hours are 24 or more.
    if (startHour >= 24) {
        startHour -= 24;
        workflowTime.add(1, 'days');
    }

    return MiscUtil.dateStartHourToFacilityTimeFormat(
            workflowTime.format(Constants.FacilityTimeFormat),
            startHour);
  }

  private buildVariableStaffingGrid(): InpatientCell[][] {
    if (!this.variableJobTitles || !this.shiftBlocks) {
      return;
    }

    let cells = [] as InpatientCell[];
    let diffCensus = this.maxCensus - this.minCensus;

    for (let rowCounter = 0; rowCounter <= diffCensus; rowCounter++) {
      let columnCounter = 0;

      this.shiftBlocks.sort(s => s.startHour).forEach(s => {
        this.variableJobTitles.forEach(j => {
          cells.push(
            this.buildStaffingGridCell(
              this.maxCensus - rowCounter,
              columnCounter.toString(),
              j.id,
              rowCounter.toString(),
              s));

          ++columnCounter;
        });
      });
    }

    this.staffingGrid = this.sortStaffingGrid(cells);
    return this.staffingGrid;
  }

  private buildStaffingGridCell(
    census: number,
    columnId: string,
    jobTitleId: number,
    rowId: string,
    shiftBlock: ShiftBlock): InpatientCell {
    let cell = new InpatientCell();

    cell.census = census;
    cell.columnId = columnId;
    cell.id = this.getCellId(+rowId, shiftBlock.id, jobTitleId);
    cell.jobTitleId = jobTitleId;
    cell.rowId = rowId;
    cell.shiftBlockId = shiftBlock.id;
    cell.startHour = shiftBlock.startHour;

    if (!(jobTitleId in shiftBlock.staffingGrid)) {
      shiftBlock.staffingGrid[jobTitleId] = {};
    }

    cell.value = shiftBlock.staffingGrid[jobTitleId][census];

    return cell;
  }

  private getCellId(census: number, shiftBlockId: number, jobTitleId: number): string {
    return census + '-' + shiftBlockId + '-' + jobTitleId;
  }

  private sortStaffingGrid(cells: InpatientCell[]): InpatientCell[][] {
    let diffCensus = this.maxCensus - this.minCensus;

    let sortedTable: InpatientCell[][] = [];

    for (let counter = 0; counter <= diffCensus; counter++) {
      let censusCount = this.maxCensus - counter;
      let row = cells.filter(c => c.census == censusCount);
      let sortedRow: InpatientCell[] = [];

      this.shiftBlocks.sort(s => s.startHour).forEach(s => {
        let columnCounter = 0;

        this.variableJobTitles.forEach(j => {
          let cell = row.find(c => c.jobTitleId == j.id && c.shiftBlockId == s.id);

          if (cell) {
            sortedRow.push(cell);
          }
          else {
            let newCell = new InpatientCell();

            newCell.census = censusCount;
            newCell.rowId = counter.toString();
            newCell.columnId = columnCounter.toString();
            newCell.shiftBlockId = s.id;
            newCell.startHour = s.startHour;
            newCell.value = s.staffingGrid[j.id][censusCount];
            sortedRow.push(newCell);

            ++columnCounter;
          }
        });
      });

      sortedTable.push(sortedRow);
    }

    return sortedTable;
  }

}
